import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { auth } from "../../../../config/firebaseConfig";
import { individualRegisterFormFields } from "../../constants/individualRegisterFormFields";
import { Button, TextInput } from "../../../../components";
import envConfig from "../../../../config/envConfig";
import { validateEmail, validatePhone } from "../../../../assets/js/validation";
import { parentRegisterSuccess } from "../../../../constant/emailTemplates/parentRegisterSuccess";
import SchoolDetails from "../components/SchoolDetails";
// import authService from "../../../../firebase/authService";
import { useDispatch } from "react-redux";
import { register, login } from "../../../../redux/features/authSlice";
import { GAEventTrack } from "../../../../utils/hooks/GA_events";
import dbService from "../../../../firebase/dbService";
import OtpModule from "../OtpModule";
import { sendEmailHandler } from "../../../../utils/helperFunctions/sendEmailHandler";
import authServiceMobile from "../../../../firebase/authServiceMobile";
import dbServiceMobile from "../../../../firebase/dbServiceMobile";
import AppStoreButtons from "../../../../components/AppStoreButtons";
import { RxCross2 } from "react-icons/rx";
import { FaWhatsapp } from "react-icons/fa";
import { parentRegisterViaSchoolLinkSuccess } from "../../../../constant/emailTemplates/parentRegisterViaSchoolLinkSuccess";
import { toast } from "react-toastify";

const IndividualRegisterForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isAiLab = searchParams.get("source") === "ai-lab";
  const demoType = searchParams.get("type");
  const isLoginMode = location.pathname.startsWith("/login");

  const getDashboardTargetUrl = () => {
    if (demoType) return `/dashboard${location.search}`;
    if (isAiLab) return "/dashboard?offer=ai-lab";
    return "/dashboard";
  };

  // School Login States
  const [showSchoolLogin, setShowSchoolLogin] = useState(false);
  const [schoolEmail, setSchoolEmail] = useState("");
  const [schoolPassword, setSchoolPassword] = useState("");
  const [schoolLoginError, setSchoolLoginError] = useState("");
  const [schoolLoginLoading, setSchoolLoginLoading] = useState(false);

  const handleSchoolLoginSubmit = async (e) => {
    e.preventDefault();
    setSchoolLoginError("");

    if (!schoolEmail.trim()) {
      setSchoolLoginError("Please enter your school email.");
      return;
    }
    if (!schoolPassword) {
      setSchoolLoginError("Please enter your password.");
      return;
    }

    setSchoolLoginLoading(true);
    try {
      await dispatch(login({ email: schoolEmail.trim(), password: schoolPassword })).unwrap();
    } catch (err) {
      setSchoolLoginError(err || "Login failed. Please check your credentials.");
    } finally {
      setSchoolLoginLoading(false);
    }
  };

  const formRef = useRef(null);
  const { schoolId } = useParams();
  const [schoolDetails, setSchoolDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showOtherLoginWays, setShowOtherLoginWays] = useState(false);
  const [error, setError] = useState(null);
  const [registerAs, setRegisterAs] = useState("parent");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Registration Stage State: "phone" | "otp" | "details"
  const [stage, setStage] = useState("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [googleUser, setGoogleUser] = useState(null);
  const [isFromLoginRedirect, setIsFromLoginRedirect] = useState(false);
  const [emailExistsMode, setEmailExistsMode] = useState(false);
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (isHovered) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev === 0 ? 1 : 0));
    }, 6000);
    return () => clearInterval(timer);
  }, [isHovered]);

  // Restore state from sessionStorage on mount (survives refreshes on Step 3)
  useEffect(() => {
    const preVerifiedPhone = sessionStorage.getItem("verifiedPhoneNumber");
    if (preVerifiedPhone) {
      setPhoneNumber(preVerifiedPhone);
      setStage("details");
      setIsFromLoginRedirect(true);
      sessionStorage.removeItem("verifiedPhoneNumber");
      return;
    }

    const savedStage = sessionStorage.getItem("practido_signup_stage");
    const savedRole = sessionStorage.getItem("practido_signup_role");
    const savedPhone = sessionStorage.getItem("practido_signup_phone");

    if (savedStage === "details" && savedPhone) {
      setStage("details");
      setRegisterAs(savedRole || "parent");
      setPhoneNumber(savedPhone);
    }
  }, []);

  // Clean form state reset whenever switching between /login and /register routes (Issue D Fix)
  useEffect(() => {
    setStage("phone");
    setError(null);
    setOtpError("");
    setOtpCode("");
    setLoading(false);
    setOtpLoading(false);
    setEmailExistsMode(false);
    setPassword("");

    if (location.pathname === "/login/school") {
      setShowSchoolLogin(true);
    } else {
      setShowSchoolLogin(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const fetchSchoolDetails = async () => {
      try {
        const data = await dbService.getdocument("schools", schoolId);
        if (data) {
          setSchoolDetails(data);
        }
      } catch (error) {
        console.log(error);
      }
    };
    schoolId && fetchSchoolDetails();
  }, [schoolId]);

  const resetFieldError = (field) => {
    setError((prev) => ({ ...prev, [field]: null }));
  };

  const handleEditPhoneNumber = () => {
    setStage("phone");
    setOtpError("");
    setOtpCode("");
    setError(null);
    setOtpLoading(false);
  };

  const sendSuccessMail = async (name, email) => {
    const emailTemplateFunction = schoolDetails
      ? parentRegisterViaSchoolLinkSuccess
      : parentRegisterSuccess;
    const { subject, emailBody } = emailTemplateFunction({
      parentName: name,
      parentEmail: email,
      schoolName: schoolDetails?.schoolName || "",
      fee: schoolDetails?.fee?.total || 99,
    });

    await sendEmailHandler({
      emailId: email,
      subject,
      body: emailBody,
    });

    GAEventTrack({
      event: "ind_success_email_sent",
      data: {
        uid: auth?.currentUser?.uid,
        userEmail: email,
      },
    });
  };

  // Step 1: Send OTP
  const sendOtp = async (phoneToUse) => {
    setOtpError("");
    const response = await fetch(`${envConfig.backendUrl}/sendWhatsappOtpV1`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        phoneNumber: phoneToUse,
        purpose: isLoginMode ? "Login" : "Registration",
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to send OTP.");
    }
    return data;
  };

  // Phone Form Submit
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setOtpError("");

    const cleanPhone = phoneNumber.trim();
    if (!cleanPhone) {
      setError({ phone: "Phone number is required." });
      return;
    }
    if (!validatePhone(cleanPhone)) {
      setError({ phone: "Please enter a valid 10 digit phone number." });
      return;
    }

    setLoading(true);
    try {
      await sendOtp(cleanPhone);
      setStage("otp");
    } catch (err) {
      setError({ phone: err.message });
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP & Check User Status
  const handleOtpSubmit = async (e, code) => {
    e.preventDefault();
    setOtpError("");
    setOtpLoading(true);

    try {
      // 1. Verify OTP
      const otpResponse = await fetch(`${envConfig.backendUrl}/verifyWhatsappOtpV1`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber: phoneNumber, otp: code }),
      });
      const otpData = await otpResponse.json();
      if (!otpResponse.ok) {
        throw new Error(otpData.message || "Invalid OTP.");
      }

      setOtpCode(code);

      // 2. Phone Login / User Status Check (Existing vs New User)
      const statusResponse = await fetch(`${envConfig.backendUrl}/phoneLoginV1`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber: phoneNumber }),
      });
      const statusData = await statusResponse.json();
      if (!statusResponse.ok) {
        throw new Error(statusData.message || "Failed to check user status.");
      }

      if (statusData.isNewUser === false && statusData.customToken) {
        // User already exists -> Immediate sign in
        const { signInWithCustomToken } = await import("firebase/auth");
        await signInWithCustomToken(auth, statusData.customToken);
        await dispatch(login({})).unwrap().catch(() => null);
        navigate(getDashboardTargetUrl());
      } else {
        // User does not exist -> Progress to Step 3 Details
        setStage("details");
        if (isLoginMode) {
          toast.info("No account found for this number. Please complete your registration.");
          setIsFromLoginRedirect(true);
        } else {
          sessionStorage.setItem("practido_signup_stage", "details");
          sessionStorage.setItem("practido_signup_role", registerAs);
          sessionStorage.setItem("practido_signup_phone", phoneNumber);
        }
      }
    } catch (err) {
      setOtpError(err.message);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleDetailsGoBack = () => {
    setStage("phone");
    sessionStorage.setItem("registration_stage", "phone");
  };

  // Step 3: Complete Sign Up
  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const cleanName = name.trim();
    const cleanEmail = email.trim();

    // If in password sub-flow (emailExistsMode), handle password verification + linking
    if (emailExistsMode) {
      if (!password) {
        setError({ password: "Password is required." });
        return;
      }

      setLoading(true);
      try {
        // 1. Verify password via REST API without mutating global client auth instance
        const apiKey = auth?.app?.options?.apiKey || envConfig.firebaseApiKey;
        const verifyRes = await fetch(
          `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${apiKey}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: cleanEmail,
              password: password,
              returnSecureToken: true,
            }),
          }
        );
        const verifyData = await verifyRes.json();

        if (!verifyRes.ok || !verifyData.idToken) {
          setError({ password: "Incorrect password. Please try again." });
          setLoading(false);
          return;
        }

        const idToken = verifyData.idToken;

        // 2. Call backend to link phone to existing account
        const linkResponse = await fetch(`${envConfig.backendUrl}/linkPhoneToExistingAccountWebV1`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            phoneNumber: phoneNumber,
            name: cleanName || undefined,
            role: registerAs,
          }),
        });
        const linkData = await linkResponse.json();
        if (!linkResponse.ok) {
          throw new Error(linkData.message || "Failed to link phone number.");
        }

        // 3. Sign in with custom token (to update global auth state and redirect)
        const { signInWithCustomToken } = await import("firebase/auth");
        await signInWithCustomToken(auth, linkData.customToken);
        await dispatch(login({})).unwrap().catch(() => null);

        sessionStorage.removeItem("practido_signup_stage");
        sessionStorage.removeItem("practido_signup_role");
        sessionStorage.removeItem("practido_signup_phone");

        navigate(getDashboardTargetUrl());
      } catch (err) {
        setError({ password: "Incorrect password. Please try again." });
      } finally {
        setLoading(false);
      }
      return;
    }

    // Normal flow: validate name and email
    const errors = {};
    if (!cleanName) errors.name = "Name is required.";
    if (!cleanEmail) errors.email = "Email is required.";
    else if (!validateEmail(cleanEmail)) errors.email = "Please enter a valid email id.";

    if (Object.keys(errors).length > 0) {
      setError(errors);
      return;
    }

    setLoading(true);
    try {
      // Check if email already exists in Auth
      const emailCheckResponse = await fetch(`${envConfig.backendUrl}/checkEmailExistsWebV1`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: cleanEmail }),
      });
      const emailCheckData = await emailCheckResponse.json();

      if (emailCheckData.exists) {
        // Email exists in Auth
        if (emailCheckData.hasPhone || emailCheckData.provider === "google.com") {
          // Has phone already attached OR is a Google account → block
          const existingPhone = emailCheckData.phone || emailCheckData.phoneNumber || emailCheckData.linkedPhone || emailCheckData.userPhone || "";
          const last4 = existingPhone ? String(existingPhone).trim().slice(-4) : "";
          const maskedPhone = last4 ? `xxxxxx${last4}` : "";

          const errorMsg = maskedPhone
            ? `This email already exists and is linked with phone number ${maskedPhone}. Please log in using that phone number or enter a different email.`
            : "This email already exists and is linked to another phone number. Please log in using that phone number or enter a different email.";

          setError({ email: errorMsg });
          setLoading(false);
          return;
        }

        if (emailCheckData.provider === "password") {
          // Password account without phone → switch to password sub-flow
          setEmailExistsMode(true);
          setLoading(false);
          toast.info("This email already has an account. Please enter your password to link your phone number.");
          return;
        }
      }

      // Email does NOT exist → proceed with normal registration
      const response = await fetch(`${envConfig.backendUrl}/registerUserWebV1`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phoneNumber: phoneNumber,
          name: cleanName,
          email: cleanEmail,
          role: registerAs,
          schoolId: schoolId || null,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Registration failed.");
      }

      const { signInWithCustomToken } = await import("firebase/auth");
      await signInWithCustomToken(auth, data.customToken);
      await dispatch(login({})).unwrap().catch(() => null);

      sessionStorage.removeItem("practido_signup_stage");
      sessionStorage.removeItem("practido_signup_role");
      sessionStorage.removeItem("practido_signup_phone");

      // Success email sending is commented out for now
      // await sendSuccessMail(cleanName, cleanEmail);

      navigate(getDashboardTargetUrl());
    } catch (err) {
      setError({ other: err.message });
    } finally {
      setLoading(false);
    }
  };

  // Google Sign-Up Flow
  const handleGoogleSignUp = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      const { signInWithPopup, GoogleAuthProvider } = await import("firebase/auth");
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      // The onAuthStateChanged useEffect listener below handles stage transitioning
    } catch (err) {
      setError({ other: err.message || "Google Sign-Up failed." });
      setGoogleLoading(false);
    }
  };

  // Listen to Firebase Auth state directly to capture Google Signups/Logins
  // and manage the stage/redirections robustly across unmounts/remounts.
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      const isGoogleUser = user?.providerData?.some((p) => p.providerId === "google.com");
      if (user && isGoogleUser) {
        setGoogleLoading(true);
        try {
          const userMeta = await dbService.getdocument("user", user.uid).catch(() => null);
          if (!userMeta) {
            // New User: show google_phone step
            setGoogleUser({
              uid: user.uid,
              email: user.email || "",
              displayName: user.displayName || "",
            });
            setStage("google_phone");
          } else {
            // Existing User: dispatch login to populate Redux and navigate to dashboard
            await dispatch(login({ email: user.email || "", password: "" })).unwrap().catch(() => null);
            navigate(getDashboardTargetUrl());
          }
        } catch (e) {
          console.error("Error checking Google user:", e);
        } finally {
          setGoogleLoading(false);
        }
      }
    });

    return () => unsubscribe();
  }, [dispatch, navigate, isAiLab]);

  const handleGooglePhoneSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const cleanPhone = phoneNumber.trim();

    if (!cleanPhone) {
      setError({ phone: "Phone number is required." });
      return;
    }
    if (!validatePhone(cleanPhone)) {
      setError({ phone: "Please enter a valid 10 digit phone number." });
      return;
    }

    setLoading(true);
    try {
      const { Timestamp } = await import("firebase/firestore");
      // Create user profiles in Firestore
      const userDoc = {
        uid: googleUser.uid,
        email: googleUser.email || "",
        name: googleUser.displayName || "",
        phone: cleanPhone,
        phoneVerified: false,
        accountType: registerAs,
        createdAt: Timestamp.now(),
      };
      await dbService.createDocumentWithId("user", userDoc, googleUser.uid);

      const roleCollection = registerAs === "student" ? "students" : "parents";
      const roleDocData = {
        uid: googleUser.uid,
        email: googleUser.email || "",
        name: googleUser.displayName || "",
        phone: cleanPhone,
        phoneVerified: false,
        createdAt: Timestamp.now(),
      };
      if (schoolId) {
        roleDocData.schoolId = schoolId;
      }
      await dbService.createDocumentWithId(roleCollection, roleDocData, googleUser.uid);

      // Force dispatch login so that userDetails/parentsDetails is updated in Redux store immediately
      await dispatch(login({ email: googleUser.email, password: "" })).unwrap();

      navigate(getDashboardTargetUrl());
    } catch (err) {
      setError({ other: err.message || "Failed to complete registration." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {schoolDetails && <SchoolDetails details={schoolDetails} />}

      {/* 2 Column View Container */}
      <div className="grid grid-cols-1 lg:grid-cols-10 gap-6 items-stretch mt-4 xl:px-12 2xl:px-20 max-w-[1440px] mx-auto w-full">

        {/* Left Column: Form Content */}
        <div className="lg:col-span-4 w-full h-full flex flex-col">
          {stage === "otp" ? (
            <div className="bg-white border border-gray-100 rounded-[2rem] p-8 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <OtpModule
                phone={phoneNumber}
                handleSubmit={handleOtpSubmit}
                sendOtp={() => sendOtp(phoneNumber)}
                loading={otpLoading}
                otpError={otpError}
                clearOtpError={() => setOtpError("")}
              />
              <button
                type="button"
                onClick={handleEditPhoneNumber}
                className="mt-6 text-sm font-bold text-[#009DFF] hover:underline block mx-auto"
              >
                ← Edit Phone Number
              </button>
            </div>
          ) : (
            <div className="bg-white border border-gray-100 rounded-[2rem] p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] h-full flex flex-col justify-start">
              {showSchoolLogin ? (
                // School Sign In Form
                <form onSubmit={handleSchoolLoginSubmit} className="w-full h-full flex flex-col justify-start">
                  <h3 className="text-xl font-extrabold text-[#111827] tracking-tight leading-tight mb-0">
                    School Sign In
                  </h3>
                  <p className="text-sm text-gray-500 font-medium" style={{ marginBottom: '2.5rem' }}>
                    Access the school administrator dashboard
                  </p>

                  <div className="form-group flex flex-col mb-4">
                    <label htmlFor="schoolEmail" className="text-sm font-bold text-gray-700 mb-1">
                      School Email *
                    </label>
                    <TextInput
                      type="email"
                      placeholderText="Enter school email"
                      inputName="schoolEmail"
                      inputValue={schoolEmail}
                      handleChange={(e) => setSchoolEmail(e.target.value)}
                    />
                  </div>

                  <div className="form-group flex flex-col mb-6">
                    <label htmlFor="schoolPassword" className="text-sm font-bold text-gray-700 mb-1">
                      Password *
                    </label>
                    <TextInput
                      type="password"
                      placeholderText="Enter password"
                      inputName="schoolPassword"
                      inputValue={schoolPassword}
                      handleChange={(e) => setSchoolPassword(e.target.value)}
                    />
                  </div>

                  {schoolLoginError && (
                    <div className="text-[#E5383B] text-xs font-semibold mb-4">{schoolLoginError}</div>
                  )}

                  <Button
                    version="secondary"
                    text="Sign In"
                    type="submit"
                    loading={schoolLoginLoading}
                    width="100%"
                    className="w-full justify-center font-extrabold"
                  />

                  <div className="text-center mt-4 flex flex-col gap-2.5">
                    <button
                      type="button"
                      onClick={() => {
                        if (location.pathname === "/login/school") {
                          navigate("/login");
                        } else {
                          setShowSchoolLogin(false);
                          setSchoolLoginError("");
                        }
                      }}
                      className="text-[#009DFF] hover:underline text-sm font-bold"
                    >
                      Sign in as Parent / Student
                    </button>

                    <button
                      type="button"
                      onClick={() => navigate("/register/school")}
                      className="text-gray-500 hover:text-gray-700 text-xs font-semibold hover:underline block mx-auto transition duration-200"
                    >
                      New School? Register here
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  {stage !== "google_phone" && !emailExistsMode && (
                    <>
                      <h3 className="text-xl font-extrabold text-[#111827] tracking-tight leading-tight mb-0">
                        {isLoginMode ? "Welcome back to Practido" : "Welcome to Practido"}
                      </h3>
                      <p className="text-sm text-gray-500 font-medium" style={{ marginBottom: '2.5rem' }}>
                        {isLoginMode ? "Sign in to access your dashboard" : "Sign up to continue your learning journey"}
                      </p>
                    </>
                  )}

                  {/* Parent/Student Switcher (Only visible in Phone Input Stage & Register Mode) */}
                  {stage === "phone" && !isLoginMode && (
                    <div className="flex justify-center" style={{ marginBottom: '1.25rem' }}>
                      <div className="relative flex p-1 bg-[#F3F4F6] rounded-full w-full max-w-xs shadow-sm">
                        {/* Sliding background indicator */}
                        <div
                          className="absolute top-1 bottom-1 rounded-full bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-all duration-300 ease-out"
                          style={{
                            left: registerAs === "parent" ? "4px" : "50%",
                            width: "calc(50% - 4px)",
                          }}
                        />

                        <button
                          type="button"
                          onClick={() => setRegisterAs("parent")}
                          className={`relative z-10 flex-1 py-2 text-sm font-bold rounded-full transition-colors duration-300 flex items-center justify-center ${registerAs === "parent"
                            ? "text-[#009DFF]"
                            : "text-gray-400 hover:text-gray-600"
                            }`}
                        >
                          I'm Parent
                        </button>

                        <button
                          type="button"
                          onClick={() => setRegisterAs("student")}
                          className={`relative z-10 flex-1 py-2 text-sm font-bold rounded-full transition-colors duration-300 flex items-center justify-center ${registerAs === "student"
                            ? "text-[#009DFF]"
                            : "text-gray-400 hover:text-gray-600"
                            }`}
                        >
                          I'm Student
                        </button>
                      </div>
                    </div>
                  )}

                  {stage === "phone" ? (
                    <form
                      onSubmit={handlePhoneSubmit}
                      className="wid-100 flex flex-col justify-start space-y-2.5"
                      noValidate
                    >
                      <div className="form-group flex flex-col">
                        <label htmlFor="phone" className="text-sm font-bold text-gray-700 flex items-center gap-1.5" style={{ marginBottom: '0.2rem' }}>
                          <FaWhatsapp className="text-[#25D366] w-4 h-4" />
                          WhatsApp Number <span className="text-[#E5383B]"> * </span>
                        </label>
                        <div className="flex gap-2 items-stretch">
                          <span className="bg-gray-100 border border-gray-200 text-gray-700 font-bold px-3 flex items-center rounded-lg text-sm select-none">
                            +91
                          </span>
                          <div className="flex-grow">
                            <TextInput
                              type="tel"
                              placeholderText="Enter 10-digit WhatsApp number"
                              inputName="phone"
                              inputValue={phoneNumber}
                              isRequired={true}
                              error={null}
                              handleChange={(e) => {
                                setPhoneNumber(e.target.value);
                                resetFieldError("phone");
                              }}
                            />
                          </div>
                        </div>
                        {error?.phone && (
                          <div className="text-xs text-[#f00] mt-1 pl-[52px] text-left">
                            {error.phone}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col gap-2 pt-0">
                        <div className="w-full">
                          <Button
                            version="secondary"
                            text="Send OTP"
                            type="submit"
                            loading={loading}
                            width="100%"
                            className="w-full justify-center font-extrabold"
                          />
                        </div>

                        {/* Legal Terms Disclosure */}
                        <p className="text-[#9CA3AF] text-xs text-center mt-4 mb-2 leading-relaxed">
                          By continuing, you agree to Practido's<br />
                          <Link to="/terms" className="text-[#009DFF] hover:text-[#005991] font-bold hover:underline">Terms of Service</Link> &{' '}
                          <Link to="/privacy" className="text-[#009DFF] hover:text-[#005991] font-bold hover:underline">Privacy Policy</Link>.
                        </p>

                        {/* Other way to log in / register Toggle Button */}
                        <div className="mt-2 text-center">
                          <button
                            type="button"
                            onClick={() => setShowOtherLoginWays((prev) => !prev)}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-xs md:text-sm font-semibold text-gray-500 hover:text-gray-800 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-full transition-all duration-200 shadow-sm focus:outline-none"
                          >
                            <span>{isLoginMode ? "Other ways to log in" : "Other ways to register"}</span>
                            <svg
                              className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-300 ${showOtherLoginWays ? "rotate-180" : ""
                                }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2.5}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>

                        {/* Expandable Google Sign-Up Option */}
                        <AnimatePresence initial={false}>
                          {showOtherLoginWays && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                              className="overflow-hidden"
                            >
                              <div className="pt-3 pb-1 flex flex-col items-center justify-center gap-3">
                                <button
                                  type="button"
                                  onClick={handleGoogleSignUp}
                                  disabled={loading || googleLoading}
                                  className="w-fit mx-auto flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-gray-700 font-bold py-2.5 px-6 rounded-xl border border-gray-200 shadow-sm transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {googleLoading ? (
                                    <span>Connecting...</span>
                                  ) : (
                                    <>
                                      <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
                                        <path
                                          fill="#4285F4"
                                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                        />
                                        <path
                                          fill="#34A853"
                                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                        />
                                        <path
                                          fill="#FBBC05"
                                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                                        />
                                        <path
                                          fill="#EA4335"
                                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                        />
                                      </svg>
                                      <span>Continue with Google</span>
                                    </>
                                  )}
                                </button>
                                
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (isLoginMode) {
                                      navigate("/login/school");
                                    } else {
                                      navigate("/register/school");
                                    }
                                  }}
                                  className="text-[#009DFF] hover:underline text-sm font-bold block mx-auto transition duration-200 mt-2"
                                >
                                  {isLoginMode ? "Are you a School? Sign in here" : "Are you a School? Register here"}
                                </button>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* Extra Passcode link removed or moved */}
                      </div>
                    </form>
                  ) : stage === "google_phone" ? (
                    // Google Phone Input Stage
                    <form
                      onSubmit={handleGooglePhoneSubmit}
                      className="wid-100 flex flex-col justify-start space-y-4"
                      noValidate
                    >
                      <h3 className="text-xl font-extrabold text-[#111827] tracking-tight leading-tight mb-0">
                        One Last Step!
                      </h3>
                      <p className="text-sm text-gray-500 font-medium" style={{ marginBottom: '1.5rem' }}>
                        Enter your WhatsApp number to receive learning updates.
                      </p>

                      {isLoginMode && (
                        <div className="flex flex-col gap-2">
                          <label className="text-xs font-bold text-gray-700 tracking-wide mb-1">
                            Are you a Parent or Student?
                          </label>
                          <div className="relative flex p-1 bg-[#F3F4F6] rounded-full w-full max-w-xs shadow-sm mb-2 select-none">
                            <div
                              className="absolute top-1 bottom-1 rounded-full bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-all duration-300 ease-out"
                              style={{
                                left: registerAs === "parent" ? "4px" : "50%",
                                width: "calc(50% - 4px)",
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => setRegisterAs("parent")}
                              className={`relative z-10 flex-1 py-2 text-xs font-bold rounded-full transition-colors duration-300 flex items-center justify-center ${registerAs === "parent" ? "text-[#009DFF]" : "text-gray-400 hover:text-gray-600"
                                }`}
                            >
                              Parent
                            </button>
                            <button
                              type="button"
                              onClick={() => setRegisterAs("student")}
                              className={`relative z-10 flex-1 py-2 text-xs font-bold rounded-full transition-colors duration-300 flex items-center justify-center ${registerAs === "student" ? "text-[#009DFF]" : "text-gray-400 hover:text-gray-600"
                                }`}
                            >
                              Student
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="form-group flex flex-col">
                        <label htmlFor="phone" className="text-sm font-bold text-gray-700 flex items-center gap-1.5" style={{ marginBottom: '0.2rem' }}>
                          <FaWhatsapp className="text-[#25D366] w-4 h-4" />
                          WhatsApp Number <span className="text-[#E5383B]"> * </span>
                        </label>
                        <div className="flex gap-2 items-stretch">
                          <span className="bg-gray-100 border border-gray-200 text-gray-700 font-bold px-3 flex items-center rounded-lg text-sm select-none">
                            +91
                          </span>
                          <div className="flex-grow">
                            <TextInput
                              type="tel"
                              placeholderText="Enter 10-digit WhatsApp number"
                              inputName="phone"
                              inputValue={phoneNumber}
                              isRequired={true}
                              error={null}
                              handleChange={(e) => {
                                setPhoneNumber(e.target.value);
                                resetFieldError("phone");
                              }}
                            />
                          </div>
                        </div>
                        {error?.phone && (
                          <div className="text-xs text-[#f00] mt-1 pl-[52px] text-left">
                            {error.phone}
                          </div>
                        )}
                      </div>

                      <div className="w-full pt-2">
                        <Button
                          version="secondary"
                          text="Complete Registration"
                          type="submit"
                          loading={loading}
                          width="100%"
                          className="w-full justify-center font-extrabold"
                        />
                      </div>
                    </form>
                  ) : (
                    // Step 3: Details Stage
                    <>
                      {emailExistsMode ? (
                        // Password sub-flow for existing email account linking
                        <form
                          onSubmit={handleDetailsSubmit}
                          className="wid-100 flex flex-col justify-start space-y-4"
                          noValidate
                        >
                          <h3 className="text-xl font-extrabold text-[#111827] tracking-tight leading-tight mb-0">
                            Email Already Exists - Enter Password
                          </h3>
                          <p className="text-sm text-gray-500 font-medium" style={{ marginBottom: '1rem' }}>
                            An account is already associated with this email address. Please enter your password to link your account.
                          </p>

                          <div className="form-group flex flex-col">
                            <label htmlFor="email" className="text-sm font-bold text-gray-700" style={{ marginBottom: '0.2rem' }}>
                              Email
                            </label>
                            <TextInput
                              type="email"
                              placeholderText="Email"
                              inputName="email"
                              inputValue={email}
                              isRequired={true}
                              isDisabled={true}
                            />
                          </div>

                          <div className="form-group flex flex-col">
                            <label htmlFor="password" className="text-sm font-bold text-gray-700" style={{ marginBottom: '0.2rem' }}>
                              Password <span className="text-[#E5383B]">*</span>
                            </label>
                            <TextInput
                              type="password"
                              placeholderText="Enter your password"
                              inputName="password"
                              inputValue={password}
                              isRequired={true}
                              error={null}
                              handleChange={(e) => {
                                setPassword(e.target.value);
                                resetFieldError("password");
                              }}
                            />
                            {error?.password && (
                              <div className="text-xs text-[#f00] mt-1 text-left">{error.password}</div>
                            )}
                          </div>

                          {error?.other && (
                            <div className="text-xs text-[#f00] mt-1 text-left">{error.other}</div>
                          )}

                          <Button
                            version="secondary"
                            text="Link Account & Continue"
                            type="submit"
                            loading={loading}
                            width="100%"
                            className="w-full justify-center font-extrabold"
                          />

                          <button
                            type="button"
                            onClick={() => {
                              setEmailExistsMode(false);
                              setPassword("");
                              setError(null);
                            }}
                            className="mt-2 text-sm font-bold text-[#009DFF] hover:underline block mx-auto"
                          >
                            ← Use a different email
                          </button>
                        </form>
                      ) : (
                        <form
                          onSubmit={handleDetailsSubmit}
                          className="wid-100 flex flex-col justify-start space-y-4"
                          noValidate
                        >
                          {isFromLoginRedirect && (
                            <div className="flex justify-center animate-fade-in" style={{ marginBottom: '0.75rem' }}>
                              <div className="relative flex p-1 bg-[#F3F4F6] rounded-full w-full max-w-xs shadow-sm">
                                <div
                                  className="absolute top-1 bottom-1 rounded-full bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-all duration-300 ease-out"
                                  style={{
                                    left: registerAs === "parent" ? "4px" : "50%",
                                    width: "calc(50% - 4px)",
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => setRegisterAs("parent")}
                                  className={`relative z-10 flex-1 py-2 text-xs font-bold rounded-full transition-colors duration-300 flex items-center justify-center ${registerAs === "parent" ? "text-[#009DFF]" : "text-gray-400 hover:text-gray-600"
                                    }`}
                                >
                                  I'm Parent
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setRegisterAs("student")}
                                  className={`relative z-10 flex-1 py-2 text-xs font-bold rounded-full transition-colors duration-300 flex items-center justify-center ${registerAs === "student" ? "text-[#009DFF]" : "text-gray-400 hover:text-gray-600"
                                    }`}
                                >
                                  I'm Student
                                </button>
                              </div>
                            </div>
                          )}
                          <div className="form-group flex flex-col">
                            <label htmlFor="name" className="text-sm font-bold text-gray-700" style={{ marginBottom: '0.2rem', display: 'block' }}>
                              {registerAs === "student" ? "Student Name" : "Parent Name"} <span className="text-[#E5383B]"> * </span>
                            </label>
                            <TextInput
                              type="text"
                              placeholderText="eg. : Sam Deo"
                              inputName="name"
                              inputValue={name}
                              isRequired={true}
                              error={error?.name}
                              handleChange={(e) => {
                                setName(e.target.value);
                                resetFieldError("name");
                              }}
                            />
                          </div>

                          <div className="form-group flex flex-col">
                            <label htmlFor="email" className="text-sm font-bold text-gray-700" style={{ marginBottom: '0.2rem', display: 'block' }}>
                              {registerAs === "student" ? "Student Email Id" : "Parent Email Id"} <span className="text-[#E5383B]"> * </span>
                            </label>
                            <TextInput
                              type="email"
                              placeholderText="eg. : example123@mail.com"
                              inputName="email"
                              inputValue={email}
                              isRequired={true}
                              error={error?.email}
                              handleChange={(e) => {
                                setEmail(e.target.value);
                                resetFieldError("email");
                              }}
                            />
                          </div>

                          <div className="flex flex-col gap-3.5 pt-2">
                            {error?.other && (
                              <div className="text-[#E5383B] text-xs font-semibold">{error?.other}</div>
                            )}

                            <div className="w-full">
                              <Button
                                version="secondary"
                                text="Complete Sign Up"
                                type="submit"
                                loading={loading}
                                width="100%"
                                className="w-full justify-center font-extrabold"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={handleDetailsGoBack}
                              disabled={loading}
                              className="text-xs font-bold text-gray-500 hover:text-gray-750 hover:underline block mx-auto mt-2"
                            >
                              ← Go Back
                            </button>
                          </div>
                        </form>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Legal Terms Disclosure moved above */}
            </div>
          )}
        </div>

        {/* Right Column: Interactive Showcase Slider */}
        <div
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className="lg:col-span-6 hidden lg:block sticky top-28 w-full h-full pb-0 select-none"
        >
          <div className="relative w-full">

            {/* Slide 1: AI Lab */}
            <div
              className={`transition-all duration-500 ease-in-out transform ${currentSlide === 0
                ? "opacity-100 translate-x-0 scale-100 pointer-events-auto relative"
                : "opacity-0 translate-x-8 scale-95 pointer-events-none absolute inset-0"
                }`}
            >
              <div className="relative w-full overflow-hidden rounded-[2.5rem] border border-gray-150/40 shadow-[0_12px_40px_rgba(0,0,0,0.03)] bg-white p-6 lg:p-8 xl:p-10 min-h-[460px] lg:min-h-[500px] xl:min-h-[540px] flex flex-col justify-center">
                {/* Background Image */}
                <img
                  src="/img/reg/reg_slider_ailab.png"
                  alt="AI Lab Background"
                  className="absolute inset-0 w-full h-full object-cover object-right"
                />

                {/* Purple Gradient Glow on Top-Left Corner */}
                <div className="absolute top-0 left-0 w-[450px] h-[450px] bg-[radial-gradient(circle_at_0%_0%,rgba(168,85,247,0.22)_0%,rgba(168,85,247,0.08)_35%,rgba(168,85,247,0.02)_60%,transparent_100%)] pointer-events-none z-[2]" />

                {/* Content Overlay */}
                <div className="relative z-10 w-[62%] xl:w-[58%] 2xl:w-[56%] flex flex-col justify-center">
                  {/* Blurry White Haze Background for Text Readability */}
                  <div className="absolute -inset-10 bg-white/70 blur-3xl rounded-full pointer-events-none z-[-1]" />
                  {/* Top Tag */}
                  <span className="bg-[#F3E8FF] text-[#9333EA] text-[10px] font-black uppercase tracking-wider px-3.5 py-1.5 rounded-full w-fit mb-3.5 shadow-sm border border-[#E9D5FF]/30">
                    PRACTIDO AI LAB
                  </span>

                  {/* Heading */}
                  <h3 className="text-xl xl:text-3xl font-black text-[#080E38] tracking-tight leading-tight mb-2.5">
                    Future Skills for Every Child
                  </h3>

                  {/* Subheading */}
                  <p className="text-[10px] xl:text-sm text-gray-500 font-semibold leading-relaxed mb-4">
                    Industry-relevant courses designed to ignite curiosity and build real-world skills.
                  </p>

                  {/* Skills pills grid */}
                  <div className="grid grid-cols-2 gap-x-2.5 gap-y-2.5 w-full">
                    {[
                      {
                        text: "AI & Robotics",
                        bg: "bg-[#F3E8FF]",
                        textCol: "text-[#9333EA]",
                        icon: (
                          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 3h6M12 3v3m-6 3h12a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6a2 2 0 012-2zm4 4h.01M14 13h.01M9 17h6" />
                          </svg>
                        )
                      },
                      {
                        text: "3D Printing & Modeling",
                        bg: "bg-[#DCFCE7]",
                        textCol: "text-[#15803D]",
                        icon: (
                          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-14L4 7m8 4v10M4 7v10l8 4" />
                          </svg>
                        )
                      },
                      {
                        text: "Coding",
                        bg: "bg-[#EFF6FF]",
                        textCol: "text-[#1D4ED8]",
                        icon: (
                          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 9l-3 3 3 3m8-6l3 3-3 3M13 5l-2 14" />
                          </svg>
                        )
                      },
                      {
                        text: "Aerodynamics",
                        bg: "bg-[#EFF6FF]",
                        textCol: "text-[#1D4ED8]",
                        icon: (
                          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                        )
                      },
                      {
                        text: "Design",
                        bg: "bg-[#FDF2F8]",
                        textCol: "text-[#BE185D]",
                        icon: (
                          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        )
                      },
                      {
                        text: "And more...",
                        bg: "bg-[#F3E8FF]",
                        textCol: "text-[#7E22CE]",
                        icon: (
                          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M6 10a2 2 0 110 4 2 2 0 010-4zm6 0a2 2 0 110 4 2 2 0 010-4zm6 0a2 2 0 110 4 2 2 0 010-4z" />
                          </svg>
                        )
                      },
                      {
                        text: "Video Editing",
                        bg: "bg-[#FFEDD5]",
                        textCol: "text-[#C2410C]",
                        icon: (
                          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )
                      },
                    ].map((skill, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-full p-1.5 pr-4 flex items-center gap-2.5 shadow-[0_2.5px_8px_rgba(0,0,0,0.035)] border-none hover:shadow-md transition-all duration-200"
                      >
                        <span className={`w-8 h-8 rounded-[12px] flex items-center justify-center flex-shrink-0 ${skill.bg} ${skill.textCol}`}>
                          {skill.icon}
                        </span>
                        <span className="truncate text-[#1E293B] font-bold text-[10px] xl:text-[11.5px] tracking-tight">{skill.text}</span>
                      </div>
                    ))}
                  </div>

                  {/* Bottom Tag */}
                  <div className="bg-[#EFF6FF] text-[#009DFF] px-3.5 py-2 rounded-xl text-[10px] font-black flex items-center gap-1.5 w-fit mt-6 shadow-sm border border-[#D5E6FF]/50">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 1.703 1 1 0 01-1.392-.174z" />
                    </svg>
                    For Classes 1 to 12
                  </div>
                </div>
              </div>
            </div>

            {/* Slide 2: Olympiad App */}
            <div
              className={`transition-all duration-500 ease-in-out transform ${currentSlide === 1
                ? "opacity-100 translate-x-0 scale-100 pointer-events-auto relative"
                : "opacity-0 -translate-x-8 scale-95 pointer-events-none absolute inset-0"
                } flex flex-col gap-4`}
            >
              {/* Card 1: Olympiad App */}
              <div className="relative w-full overflow-hidden rounded-[2.5rem] border border-gray-150/40 shadow-[0_12px_40px_rgba(0,0,0,0.03)] bg-white p-6 lg:p-8 xl:p-10 min-h-[340px] lg:min-h-[380px] xl:min-h-[420px] flex flex-col justify-center">
                {/* Background Image */}
                <img
                  src="/img/reg/reg_slider_app.png"
                  alt="Olympiad App Background"
                  className="absolute inset-0 w-full h-full object-cover object-right"
                />

                {/* Content Overlay */}
                <div className="relative z-10 w-[62%] xl:w-[58%] 2xl:w-[56%] flex flex-col justify-center">
                  {/* Blurry White Haze Background for Text Readability */}
                  <div className="absolute -inset-10 bg-white/75 blur-3xl rounded-full pointer-events-none z-[-1]" />
                  {/* Top Tag */}
                  <span className="bg-[#E0F2FE] text-[#0369A1] text-[10px] font-black uppercase tracking-wider px-3.5 py-1.5 rounded-full w-fit mb-3.5 shadow-sm border border-[#BAE6FD]/30">
                    PRACTIDO OLYMPIAD APP
                  </span>

                  {/* Heading */}
                  <h3 className="text-xl xl:text-3xl font-black text-[#080E38] tracking-tight leading-tight mb-2.5">
                    Practice. Compete. Improve.
                  </h3>

                  {/* Subheading */}
                  <p className="text-[10px] xl:text-sm text-gray-500 font-semibold leading-relaxed mb-4">
                    Olympiad preparation made smart, fun and effective.
                  </p>

                  {/* Features list */}
                  <div className="flex flex-col gap-2.5">
                    {[
                      "Maths, Science, English & Computers Olympiad Preparation",
                      "Topic-wise Practice Tests",
                      "Leaderboards & Achievements",
                      "Parent Progress Dashboard",
                      "Performance Analytics",
                    ].map((feature, index) => (
                      <div key={index} className="flex items-center gap-2.5">
                        <span className="w-4 h-4 rounded-full bg-[#009DFF] flex items-center justify-center text-white flex-shrink-0 shadow-[0_2px_4px_rgba(0,157,255,0.3)]">
                          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                        <span className="text-[10px] xl:text-sm font-bold text-gray-700 leading-tight">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Card 2: Horizontal Trust Card */}
              <div className="w-full bg-white rounded-[2rem] border border-gray-150/40 shadow-[0_12px_40px_rgba(0,0,0,0.03)] p-4 xl:p-5">
                <div className="grid grid-cols-2 2xl:grid-cols-4 gap-y-4 gap-x-3 xl:gap-x-4 w-full">

                  {/* Item 1 */}
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-full bg-[#F3E8FF] flex items-center justify-center text-[#9333EA] flex-shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-[10px] xl:text-[11px] text-gray-500 font-semibold leading-tight">Trusted by</span>
                      <span className="text-[11px] xl:text-[12px] font-black text-[#080E38] leading-tight">50,000+ Students</span>
                    </div>
                  </div>

                  {/* Item 2 */}
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-full bg-[#FAE8FF] flex items-center justify-center text-[#D946EF] flex-shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-[10px] xl:text-[11px] text-gray-500 font-semibold leading-tight">Expert Mentors</span>
                      <span className="text-[11px] xl:text-[12px] font-black text-[#080E38] leading-tight">& Instructors</span>
                    </div>
                  </div>

                  {/* Item 3 */}
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-full bg-[#D1FAE5] flex items-center justify-center text-[#10B981] flex-shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-[10px] xl:text-[11px] text-gray-500 font-semibold leading-tight">Safe & Secure</span>
                      <span className="text-[11px] xl:text-[12px] font-black text-[#080E38] leading-tight">Learning</span>
                    </div>
                  </div>

                  {/* Item 4 */}
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-full bg-[#FEF3C7] flex items-center justify-center text-[#D97706] flex-shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.907c.961 0 1.36 1.246.588 1.81l-3.97 2.883a1 1 0 00-.364 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.971-2.883a1 1 0 00-1.18 0l-3.97 2.883c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.364-1.118L2.98 10.1c-.773-.564-.373-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                      </svg>
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-[10px] xl:text-[11px] text-gray-500 font-semibold leading-tight">Fun Learning</span>
                      <span className="text-[11px] xl:text-[12px] font-black text-[#080E38] leading-tight">Experience</span>
                    </div>
                  </div>

                </div>
              </div>
            </div>

          </div>

          {/* Slider Dots Indicator */}
          <div className="mt-6 flex justify-center gap-2.5">
            <button
              type="button"
              onClick={() => setCurrentSlide(0)}
              className={`h-2 rounded-full transition-all duration-305 ${currentSlide === 0
                ? "w-6 bg-[#009DFF]"
                : "w-2 bg-gray-300 hover:bg-gray-400"
                }`}
              aria-label="Go to slide 1"
            />
            <button
              type="button"
              onClick={() => setCurrentSlide(1)}
              className={`h-2 rounded-full transition-all duration-305 ${currentSlide === 1
                ? "w-6 bg-[#009DFF]"
                : "w-2 bg-gray-300 hover:bg-gray-400"
                }`}
              aria-label="Go to slide 2"
            />
          </div>
        </div>
      </div>

    </>
  );
};

export default IndividualRegisterForm;
